import { useEffect, useRef, useState } from 'react';

import {
  EMPTY_WORKSPACE_AGENT_MENTION_EXPANSION_STATE,
  EMPTY_WORKSPACE_AGENT_MENTION_PENDING_COUNT_NODE_IDS,
  toWorkspaceAgentMentionExpansionAnnouncement,
  toWorkspaceAgentMentionExpansionState,
  type WorkspaceAgentMentionExpansionState,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/toWorkspaceAgentMentionExpansionAnnouncement';
import {
  type WorkspaceAgentMentionItem,
  type WorkspaceAgentMentionSourceKey,
  type WorkspaceAgentMentionTreeItem,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';

export type UseWorkspaceAgentMentionExpansionAnnouncementParams = {
  activeSourceKey: WorkspaceAgentMentionSourceKey;
  /** Only this row's own children can say whether it is still uncounted. */
  hasPendingChildCount: (item: WorkspaceAgentMentionTreeItem) => boolean;
  items: WorkspaceAgentMentionItem[];
  query: string;
};

/** What the live region says about the row the user just opened or closed, if any. */
export const useWorkspaceAgentMentionExpansionAnnouncement = ({
  activeSourceKey,
  hasPendingChildCount,
  items,
  query,
}: UseWorkspaceAgentMentionExpansionAnnouncementParams) => {
  const [expansionMessage, setExpansionMessage] = useState('');
  const expansionStateRef = useRef<WorkspaceAgentMentionExpansionState>({});
  const pendingCountNodeIdsRef = useRef<string[]>([]);
  /** Which source and query the state above was gathered under — a change discards all of it. */
  const listKeyRef = useRef<string>();

  useEffect(() => {
    /**
     * What was open, and what is still owed a count, belong to the list they were opened in. A new
     * query or another source replaces that list wholesale, so neither carries across: an owed row
     * announced against rows it was never part of would speak a count for a toggle nobody made.
     */
    const listKey = `${activeSourceKey}:${query}`;
    const hasListChanged = listKeyRef.current !== listKey;

    listKeyRef.current = listKey;

    const { message, pendingCountNodeIds } = toWorkspaceAgentMentionExpansionAnnouncement({
      hasPendingChildCount,
      items,
      pendingCountNodeIds: hasListChanged
        ? EMPTY_WORKSPACE_AGENT_MENTION_PENDING_COUNT_NODE_IDS
        : pendingCountNodeIdsRef.current,
      previousExpansion: hasListChanged ? EMPTY_WORKSPACE_AGENT_MENTION_EXPANSION_STATE : expansionStateRef.current,
    });

    setExpansionMessage(message);
    expansionStateRef.current = toWorkspaceAgentMentionExpansionState(items);
    pendingCountNodeIdsRef.current = pendingCountNodeIds;
  }, [activeSourceKey, hasPendingChildCount, items, query]);

  return { expansionMessage };
};
